import { Add, Delete, SaveOutlined } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    InputAdornment,
    Popper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import LineTaxCell from '../../../common/Tax/LineTaxCell';
import { addInvoiceFeesApi, updateInvoiceFeesApi } from '../../../redux/Invoice/action';
import FeesSvg from '../../../utility/Svg/FeesSvg';
import { handlePreventKeyDownDecimal } from '../../../utility/helpers/commonFunction';
import { NumericEngine, Raw } from '../../../utility/helpers/numericEngine';
import { DOCUMENT_TAX_TYPE, LINE_TAX_KIND } from '../../../utility/helpers/taxCalculation';

// ─── column config ──────────────────────────────────────────────────────────
// Single source of truth for column widths — drives <colgroup> AND the header
// row. The body must render exactly one <TableCell> per entry here, in the
// same order (7 columns with tax, 6 without), or table-layout:fixed breaks.
const getFeeColumns = (isNewTaxVersion) => [
    { key: 'no', label: 'No.', width: 56 },
    { key: 'description', label: 'Description', width: null }, // flexible, takes remaining space
    { key: 'type', label: 'Type', width: 116 },
    { key: 'value', label: 'Value', width: 120 },
    { key: 'amount', label: 'Amount', width: 120 },
    ...(isNewTaxVersion ? [{ key: 'tax', label: 'Tax', width: 80 }] : []),
    { key: 'actions', label: '', width: 80 }, // save + delete icons, combined
];

// ─── helpers ────────────────────────────────────────────────────────────────

const WidePopper = (props) => <Popper {...props} style={{ width: 280, zIndex: 1400 }} placement="bottom-start" />;

const TypeToggle = ({ value, onChange, disabled }) => {
    const tray = {
        display: 'flex',
        width: '96px',
        padding: '3px 4px',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '48px',
        border: '1px solid rgba(0, 0, 0, 0.10)',
        background: '#FFF',
        boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
    };
    const btn = {
        flex: 1,
        height: 32,
        borderRadius: '38px',
        fontFamily: 'Roboto',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '143%',
        letterSpacing: '0.17px',
        textAlign: 'right',
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: 'none',
        background: 'transparent',
        color: '#212121',
        transition: 'background 0.12s, color 0.12s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
    const active = { background: '#006CDD', color: '#FFF' };

    return (
        <Stack direction="row" sx={tray}>
            <Box component="span" sx={{ ...btn, ...(value === 'fixed' ? active : {}) }} onClick={() => !disabled && onChange('fixed')}>
                $
            </Box>
            <Box component="span" sx={{ ...btn, ...(value === 'percentage' ? active : {}) }} onClick={() => !disabled && onChange('percentage')}>
                %
            </Box>
        </Stack>
    );
};

// ─── FeeName cell ────────────────────────────────────────────────────────────
// Always underlined Autocomplete (saved + new): 16px / 400, fully editable.
// Clearing the name clears _id so the dropdown works again.
//
const feeNameInputSx = {
    '& .MuiInputBase-input': {
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '143%',
        color: '#212121',
    },
    '& .MuiInput-underline:before': { borderBottomColor: 'rgba(0, 0, 0, 0.42)' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'rgba(0, 0, 0, 0.42)' },
};

const FeeOptionRow = (props, option) => {
    const uid = String(option._id || option.name || 'opt').replace(/[^a-zA-Z0-9_-]/g, '');
    const c0 = `feeOptClip0_${uid}`;
    const c1 = `feeOptClip1_${uid}`;
    return (
        <Box
            component="li"
            {...props}
            key={option._id || option.name}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '12px 16px !important',
                background: '#fff',
                '&:hover': {
                    background: 'rgba(33, 150, 243, 0.04)!important',
                },
            }}
        >
            <div style={{ width: '24px', height: '24px' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath={`url(#${c0})`}>
                        <circle cx="18" cy="6" r="3" fill="white" />
                        <path
                            d="M2.59 11.58L11.59 2.58C11.95 2.22 12.45 2 13 2H20C21.1 2 22 2.9 22 4V11C22 11.55 21.78 12.05 21.41 12.42L12.41 21.42C12.05 21.78 11.55 22 11 22C10.45 22 9.95 21.78 9.59 21.41L2.59 14.41C2.22 14.05 2 13.55 2 13C2 12.45 2.23 11.94 2.59 11.58ZM18.5 7C19.33 7 20 6.33 20 5.5C20 4.67 19.33 4 18.5 4C17.67 4 17 4.67 17 5.5C17 6.33 17.67 7 18.5 7Z"
                            fill="#006CDD"
                        />
                        <g clipPath={`url(#${c1})`}>
                            <path
                                d="M12 8C9.79095 8 8 9.79095 8 12C8 14.2091 9.79095 16 12 16C14.2091 16 16 14.2091 16 12C16 9.79095 14.2091 8 12 8ZM13.149 13.577C12.9745 13.7893 12.7473 13.9325 12.4823 14.0049C12.3671 14.0362 12.3144 14.0971 12.321 14.2173C12.3259 14.3358 12.321 14.4527 12.3193 14.5712C12.3193 14.6765 12.265 14.7325 12.1613 14.7358C12.0938 14.7374 12.0263 14.7391 11.9588 14.7391C11.8996 14.7391 11.8403 14.7391 11.7811 14.7374C11.6691 14.7358 11.6165 14.6716 11.6165 14.563C11.6148 14.4774 11.6148 14.3901 11.6148 14.3045C11.6132 14.1136 11.6066 14.107 11.4239 14.0774C11.1901 14.0395 10.9597 13.9868 10.7457 13.8831C10.5778 13.8008 10.5597 13.7597 10.6074 13.5819C10.6436 13.4502 10.6798 13.3185 10.721 13.1885C10.7506 13.093 10.7786 13.0502 10.8296 13.0502C10.8593 13.0502 10.8971 13.065 10.9481 13.0914C11.1852 13.2148 11.437 13.2839 11.7021 13.3169C11.7465 13.3218 11.7909 13.3251 11.8354 13.3251C11.9588 13.3251 12.079 13.3021 12.1959 13.251C12.4905 13.1226 12.5366 12.7819 12.2881 12.5778C12.2041 12.5086 12.107 12.4576 12.0066 12.4132C11.7481 12.2996 11.4798 12.214 11.2362 12.0675C10.8412 11.8305 10.5909 11.5062 10.6206 11.0255C10.6535 10.4823 10.9613 10.1432 11.4601 9.96214C11.6658 9.88807 11.6675 9.88971 11.6675 9.67572C11.6675 9.60329 11.6658 9.53086 11.6691 9.45679C11.6741 9.29547 11.7004 9.26749 11.8617 9.26255H12.0494C12.3556 9.26255 12.3556 9.27572 12.3572 9.60658C12.3588 9.85021 12.3588 9.85021 12.6008 9.88807C12.7868 9.9177 12.963 9.97202 13.1342 10.0477C13.228 10.0889 13.2642 10.1547 13.2346 10.2551C13.1918 10.4033 13.1506 10.5531 13.1045 10.6996C13.0749 10.7885 13.0469 10.8296 12.9942 10.8296C12.9646 10.8296 12.9284 10.8181 12.8823 10.7951C12.6453 10.6798 12.3967 10.6239 12.1366 10.6239C12.1037 10.6239 12.0691 10.6255 12.0362 10.6272C11.9588 10.6321 11.8831 10.642 11.8107 10.6733C11.5539 10.7852 11.5128 11.0683 11.7317 11.2428C11.842 11.3317 11.9687 11.3942 12.0988 11.4486C12.3259 11.5424 12.5531 11.6329 12.7687 11.7514C13.4469 12.13 13.6313 12.9909 13.149 13.577Z"
                                fill="white"
                            />
                        </g>
                    </g>
                    <defs>
                        <clipPath id={c0}>
                            <rect width="24" height="24" fill="white" />
                        </clipPath>
                        <clipPath id={c1}>
                            <rect width="8" height="8" fill="white" transform="translate(8 8)" />
                        </clipPath>
                    </defs>
                </svg>
            </div>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                <Typography fontSize={14} fontWeight={600}>
                    {option.name}
                </Typography>
                <Typography fontSize={13} color="#848484" letterSpacing={0.17}>
                    {option.calculationType === 'fixed_amount'
                        ? `$${Number(option.amount ?? 0).toFixed(2)} fixed fee`
                        : `${option.percentage ?? 0}% of invoice`}
                </Typography>
            </Box>
        </Box>
    );
};

const FeeNameCell = ({ row, idx, availableOptions, updateRow }) => (
    <Autocomplete
        freeSolo
        options={availableOptions}
        PopperComponent={WidePopper}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.name || '')}
        inputValue={row.name}
        onInputChange={(_, v, reason) => {
            if (reason === 'reset') return;
            const patch = { name: v };
            if (!v.trim()) {
                patch._id = null;
            }
            updateRow(idx, patch);
        }}
        onChange={(_, v) => {
            if (typeof v === 'string') {
                if (v.trim()) {
                    updateRow(idx, { name: v });
                }
                return;
            }
            if (v?.name) {
                updateRow(idx, {
                    _id: v._id,
                    name: v.name,
                    feeType: v.calculationType === 'fixed_amount' ? 'fixed' : 'percentage',
                    value: String(v.calculationType === 'fixed_amount' ? (v.amount ?? '') : (v.percentage ?? '')),
                    isDefaultInvoice: v.isDefault ?? v.isDefaultInvoice ?? false,
                    isDefaultEstimate: v.isDefaultEstimate ?? false,
                    isDefaultWorkOrder: v.isDefaultWorkOrder ?? false,
                    _origName: v.name,
                    _origType: v.calculationType === 'fixed_amount' ? 'fixed' : 'percentage',
                    _origVal: String(v.calculationType === 'fixed_amount' ? (v.amount ?? '') : (v.percentage ?? '')),
                    forceSaveEnabled: false,
                });
            }
        }}
        renderInput={(params) => <TextField {...params} placeholder="Add Fee Name" variant="standard" sx={feeNameInputSx} />}
        renderOption={FeeOptionRow}
    />
);

// ─── Main component ──────────────────────────────────────────────────────────

const FeesSection = ({
    feesList = [],
    selectedFees = [],
    setSelectedFees,
    paymentDetails,
    fetchFeesList,
    isNewTaxVersion = false,
    shopTaxesData,
    documentType = DOCUMENT_TAX_TYPE.INVOICE,
    globalTaxesBreakdown = null,
}) => {
    const feeToRow = useCallback((fee) => {
        const calcType = fee.calculationType || 'fixed_amount';
        const feeType = calcType === 'fixed_amount' ? 'fixed' : 'percentage';
        const value = feeType === 'fixed' ? String(fee.amount ?? '') : String(fee.percentage ?? '');
        return {
            _id: fee._id || null,
            name: fee.name || '',
            feeType,
            value,
            isSaving: false,
            isDefaultInvoice: fee.isDefaultInvoice ?? false,
            isDefaultEstimate: fee.isDefaultEstimate ?? false,
            isDefaultWorkOrder: fee.isDefaultWorkOrder ?? false,
            _origName: fee.name || '',
            _origType: feeType,
            _origVal: value,
            forceSaveEnabled: fee.forceSaveEnabled ?? false,
            taxesBreakdown: fee.taxesBreakdown,
        };
    }, []);

    const [rows, setRows] = useState(() => selectedFees.map(feeToRow));
    const lastPushedRef = useRef(null);

    useEffect(() => {
        const currentDataStr = JSON.stringify(selectedFees);
        if (lastPushedRef.current !== currentDataStr) {
            setRows(selectedFees.map(feeToRow));
            lastPushedRef.current = currentDataStr;
        }
    }, [selectedFees, feeToRow]);

    useEffect(() => {
        const nextFees = rows.map((r) => ({
            _id: r._id,
            name: r.name,
            percentage: r.feeType === 'percentage' ? Number(r.value) : 0,
            amount: r.feeType === 'fixed' ? Number(r.value) : 0,
            calculationType: r.feeType === 'fixed' ? 'fixed_amount' : 'percentage',
            taxesBreakdown: r.taxesBreakdown,
        }));

        const nextDataStr = JSON.stringify(nextFees);
        if (lastPushedRef.current !== nextDataStr) {
            lastPushedRef.current = nextDataStr;
            setSelectedFees(nextFees);
        }
    }, [rows, setSelectedFees]);

    const updateRow = (idx, patch) => {
        setRows((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], ...patch };
            return next;
        });
    };

    const handleSave = async (idx) => {
        const row = rows[idx];
        if (!row.name?.trim() || !row.value) {
            return toast.error('Please enter a name and value.');
        }

        updateRow(idx, { isSaving: true });
        try {
            const payload = {
                name: row.name.trim(),
                type: 'invoice',
                calculationType: row.feeType === 'fixed' ? 'fixed_amount' : 'percentage',
                isDefaultInvoice: row._id ? (row.isDefaultInvoice ?? false) : false,
                isDefaultEstimate: row._id ? (row.isDefaultEstimate ?? false) : false,
                // Preserved so editing a fee from a document never clears its work order default.
                isDefaultWorkOrder: row._id ? (row.isDefaultWorkOrder ?? false) : false,
            };

            if (row.feeType === 'fixed') {
                payload.amount = Number(row.value);
            } else {
                payload.percentage = Number(row.value);
            }

            const resp = row._id ? await updateInvoiceFeesApi(row._id, payload) : await addInvoiceFeesApi(payload);

            if (resp && !resp.error) {
                const saved = resp.data || resp;
                updateRow(idx, {
                    _id: saved._id || row._id,
                    isSaving: false,
                    _origName: row.name,
                    _origType: row.feeType,
                    _origVal: row.value,
                });
                toast.success('Fee saved successfully');
                fetchFeesList?.();
            } else {
                updateRow(idx, { isSaving: false });
            }
        } catch {
            updateRow(idx, { isSaving: false });
        }
    };

    const handleDeleteRow = (idx) => {
        setRows((prev) => prev.filter((_, i) => i !== idx));
    };

    const subTotal = Number(paymentDetails?.subTotal) || 0;
    const usedIds = new Set(rows.filter((r) => r._id).map((r) => r._id));
    const availableOptions = feesList.filter((f) => !usedIds.has(f._id));
    const handleAddFeeRow = () => setRows((prev) => [...prev, feeToRow({})]);

    const columns = getFeeColumns(isNewTaxVersion);

    return (
        <Grid item container xs={12} mt={3} className="customBoxWrapper" px={0}>
            <Grid container px={3} py={1} alignItems="center" justifyContent="space-between">
                <Box className="accordianTitleWrapper">
                    <FeesSvg />
                    <Typography className="accordianTitle">Fees</Typography>
                </Box>
                {!selectedFees.length > 0 && (
                    <Button variant="outlined" color="primary" size="large" startIcon={<Add />} onClick={handleAddFeeRow}>
                        FEE
                    </Button>
                )}
            </Grid>

            {rows.length > 0 && (
                <Grid container px={2} mt={3} style={{ paddingLeft: '0', paddingRight: '0' }}>
                    <TableContainer>
                        <Table
                            aria-label="caption table"
                            sx={{
                                width: '100%',
                                tableLayout: 'fixed',
                            }}
                        >
                            <colgroup>
                                {columns.map((col) => (
                                    <col key={col.key} style={col.width ? { width: col.width } : undefined} />
                                ))}
                            </colgroup>

                            <TableHead sx={{ bgcolor: '#F8F8F8', height: 40 }}>
                                <TableRow>
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.key}
                                            sx={{
                                                ...(col.width ? { width: `${col.width}px` } : {}),
                                                fontSize: 14,
                                                fontWeight: 500,
                                                color: '#212121',
                                                border: 'none',
                                                padding: col.key === 'no' ? '8px 10px 2px 16px' : col.key === 'actions' ? 0 : '8px 10px',
                                            }}
                                        >
                                            {/* <p style={{ padding: 0, margin: 0, borderRight: col.key === 'type' ? '1px solid rgba(0, 0, 0, 0.10)' : 0 }}> */}
                                            {col.label}
                                            {/* </p> */}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.map((row, idx) => {
                                    const isNew = !row._id;
                                    const hasData = row.name?.trim() && Number(row.value) > 0;
                                    const isChanged = row.name !== row._origName || row.feeType !== row._origType || row.value !== row._origVal;
                                    const saveDisabled = row.isSaving || !hasData || (!isNew && !isChanged);

                                    const amt =
                                        row.feeType === 'fixed'
                                            ? Number(row.value) || 0
                                            : Number(row.value) && subTotal
                                              ? Number(NumericEngine.divide(Raw.mul(subTotal, row.value), 100))
                                              : 0;

                                    const valueNum = Number(row.value);
                                    const valueAdornmentHasAmount = String(row.value ?? '').trim() !== '' && !Number.isNaN(valueNum) && valueNum !== 0;
                                    const valueAdornmentColor = valueAdornmentHasAmount ? 'rgba(0, 0, 0, 0.87)' : '#939393';

                                    return (
                                        <TableRow key={idx} sx={{ borderBottom: '1px solid #F0F0F0', '&:last-child': { borderBottom: 'none' } }}>
                                            {/* No. */}
                                            <TableCell sx={{ fontWeight: '600', padding: '12px 10px 12px 16px' }}>{idx + 1}</TableCell>

                                            {/* Description */}
                                            <TableCell sx={{ padding: '12px 10px' }}>
                                                <FeeNameCell row={row} idx={idx} availableOptions={availableOptions} updateRow={updateRow} />
                                            </TableCell>

                                            {/* Type */}
                                            <TableCell sx={{ padding: '12px 10px' }}>
                                                <TypeToggle value={row.feeType} onChange={(v) => updateRow(idx, { feeType: v })} />
                                            </TableCell>

                                            {/* Value */}
                                            <TableCell sx={{ padding: '12px 10px' }}>
                                                <TextField
                                                    value={row.value}
                                                    size="small"
                                                    variant="outlined"
                                                    placeholder="0.00"
                                                    onKeyDown={handlePreventKeyDownDecimal}
                                                    onChange={(e) => {
                                                        let v = e.target.value.replace(/[^\d.]/g, '');
                                                        // allow only one dot
                                                        const parts = v.split('.');
                                                        if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
                                                        // cap to 2 decimal places
                                                        if (parts[1]?.length > 2) v = parts[0] + '.' + parts[1].slice(0, 2);
                                                        updateRow(idx, { value: v });
                                                    }}
                                                    InputProps={{
                                                        startAdornment:
                                                            row.feeType === 'fixed' ? (
                                                                <InputAdornment position="start" sx={{ alignItems: 'center' }}>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            height: '100%',
                                                                            fontSize: 16,
                                                                            color: valueAdornmentColor,
                                                                            mr: '-7px',
                                                                        }}
                                                                    >
                                                                        $
                                                                    </Box>
                                                                </InputAdornment>
                                                            ) : null,
                                                        endAdornment:
                                                            row.feeType === 'percentage' ? (
                                                                <InputAdornment position="end" sx={{ alignItems: 'center' }}>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            height: '100%',
                                                                            fontSize: 16,
                                                                            letterSpacing: 0.17,
                                                                            lineHeight: '143%',
                                                                            color: valueAdornmentColor,
                                                                            ml: '-4px',
                                                                        }}
                                                                    >
                                                                        %
                                                                    </Box>
                                                                </InputAdornment>
                                                            ) : null,
                                                        sx: {
                                                            height: 40,
                                                            borderRadius: '8px',
                                                            fontSize: 16,
                                                            letterSpacing: '0.17px',
                                                            boxShadow: '0px 2px 3px 0px rgba(0,0,0,0.06)',
                                                            paddingLeft: row.feeType === 'fixed' ? '8px' : '0',
                                                            paddingRight: row.feeType === 'percentage' ? '8px' : '12px',
                                                        },
                                                    }}
                                                    fullWidth
                                                    sx={{
                                                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                                            WebkitAppearance: 'none',
                                                            margin: 0,
                                                        },
                                                        '& input[type=number]': { MozAppearance: 'textfield' },
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Amount */}
                                            <TableCell sx={{ padding: '12px 10px', fontWeight: 600, fontSize: 16 }}>
                                                {NumericEngine.format(amt || 0, {
                                                    formatCurrency: true,
                                                    trailingZeros: amt ? true : false,
                                                })}
                                            </TableCell>

                                            {/* Tax (optional) */}
                                            {isNewTaxVersion && (
                                                <TableCell sx={{ padding: '12px 10px' }}>
                                                    <LineTaxCell
                                                        taxesBreakdown={row.taxesBreakdown}
                                                        lineAmount={amt}
                                                        documentType={documentType}
                                                        shopTaxesData={shopTaxesData}
                                                        globalTaxesBreakdown={globalTaxesBreakdown}
                                                        kind={LINE_TAX_KIND.FEES}
                                                        editable={isNewTaxVersion}
                                                        onChange={(next) => updateRow(idx, { taxesBreakdown: next })}
                                                    />
                                                </TableCell>
                                            )}

                                            {/* Actions — save + delete combined into a single cell,
                                                matching the "actions" column defined in getFeeColumns */}
                                            <TableCell sx={{ padding: '0' }}>
                                                <IconButton disabled={saveDisabled} size="small" onClick={() => handleSave(idx)}>
                                                    <SaveOutlined />
                                                </IconButton>

                                                <IconButton color="error" size="small" onClick={() => handleDeleteRow(idx)}>
                                                    <Delete />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {selectedFees?.length > 0 && (
                        <Button variant="outlined" color="primary" size="large" startIcon={<Add />} onClick={handleAddFeeRow} sx={{ mt: '16px', ml: '16px' }}>
                            FEE
                        </Button>
                    )}
                </Grid>
            )}
        </Grid>
    );
};

export default FeesSection;
